import { CommonModule, DatePipe } from '@angular/common';
import { Component, ElementRef, ViewChild, computed, effect, inject, input, output, signal, untracked } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatMenuTrigger } from '@angular/material/menu';
import { TranslocoModule, TranslocoService } from '@jsverse/transloco';
import { NgScrollbarModule } from 'ngx-scrollbar';
import { Observable, firstValueFrom, of, switchMap } from 'rxjs';
import { MaterialModule } from '@ukam/ui-kit/material.module';
import { AppStatusBadgeSmComponent } from '@ukam/ui-kit/status-badge-sm/status-badge-sm.component';
import { AppDialogBoxService } from '@ukam/ui-kit/dialog-box/dialog-box.service';
import { AppDialogBoxResult } from '@ukam/ui-kit/dialog-box/dialog-box.models';
import { ResultService } from '@ukam/core/services/result.service';
import { formatBytes } from '@ukam/core/functions/bytes.functions';
import { CodeKeyName } from '@ukam/core/models/code-key-name';
import { StatusField } from '@ukam/core/models/status-field';
import { StoredFile } from '@ukam/core/storages/models/stored-file';
import { StoredFolder } from '@ukam/core/storages/models/stored-folder';
import { StoredFileApiService } from '@ukam/core/storages/services/stored-file-api.service';
import { StoredFolderApiService } from '@ukam/core/storages/services/stored-folder-api.service';
import { StoredFileUploadService } from '@ukam/core/storages/services/stored-file-upload.service';

export type ExplorerViewMode = 'list' | 'details' | 'icons' | 'tiles';
export type ExplorerSortBy = 'name' | 'size' | 'date';

/** Storage (disco) como o explorador precisa vê-lo. Compatível com o modelo Storage do admin. */
export interface ExplorerStorage {
  id?: string | null;
  name?: string | null;
  provider?: CodeKeyName | null;
  status?: StatusField | null;
  isDefault?: boolean | null;
  totalBytes?: number | null;
  usedBytes?: number | null;
  reservedBytes?: number | null;
}

/** Linha unificada de pasta ou arquivo para as quatro visualizações. */
export interface ExplorerItem {
  kind: 'folder' | 'file';
  id: string;
  name: string;
  sizeBytes: number;
  date: string | null;
  icon: string;
  typeName: string | null;
  folder?: StoredFolder;
  file?: StoredFile;
}

/** Nó do painel de árvore: um storage (disco) ou uma pasta. */
export interface ExplorerTreeNode {
  kind: 'storage' | 'folder';
  id: string;
  name: string;
  storage: ExplorerStorage;
  folder: StoredFolder | null;
  parent: ExplorerTreeNode | null;
  children: ExplorerTreeNode[] | null;
  expanded: boolean;
  loading: boolean;
}

/** Destino de uma ação (upload, nova pasta): disco + pasta (nula = raiz do disco). */
export interface ExplorerTarget {
  storage: ExplorerStorage | null;
  folder: StoredFolder | null;
}

const VIEW_MODE_STORAGE_KEY = 'ukam.file-explorer.view-mode';

/** Máximo de itens (pastas + arquivos) por página. */
const PAGE_SIZE = 200;

/**
 * Explorador de arquivos no estilo Windows/Drive. Painel esquerdo em árvore ("Este Computador"):
 * cada Storage é um disco e as pastas expandem sob ele; painel direito com o conteúdo da pasta em
 * quatro visualizações (lista, detalhes, ícones, blocos), breadcrumb, upload por botão ou
 * arrastar-e-soltar, mover por arrasto, menu de contexto e painel de detalhes.
 *
 * Feature e parentNid são filtros opcionais e independentes (arquivos podem ou não ter vínculo).
 *
 * Uso com discos (gerenciador):   <app-file-explorer [storages]="storages()"></app-file-explorer>
 * Uso embutido em um cadastro:    <app-file-explorer feature="loan" [parentNid]="model.nid"></app-file-explorer>
 */
@Component({
  selector: 'app-file-explorer',
  templateUrl: './file-explorer.component.html',
  imports: [CommonModule, FormsModule, TranslocoModule, MaterialModule, NgScrollbarModule, AppStatusBadgeSmComponent, DatePipe],
})
export class AppFileExplorerComponent {
  /** Discos exibidos na árvore. Vazio = sem painel de discos (storage resolvido pela hierarquia). */
  storages = input<ExplorerStorage[]>([]);
  /** Storage fixo quando não há lista de discos (modo embutido). */
  storageId = input<string | null>(null);
  /** Feature e parentNid são opcionais e independentes; vazios = itens sem esse vínculo. */
  feature = input<string | null>(null);
  parentNid = input<number | null | undefined>(null);
  /** Tipo de negócio (StoredFileType) aplicado aos uploads. */
  fileType = input<string | null>(null);
  readonly = input(false);
  /** Espaço ocupado mudou (upload/exclusão); o host deve recarregar `storages` para atualizar os totais. */
  usageChanged = output<void>();

  @ViewChild('fileInput') fileInput!: ElementRef<HTMLInputElement>;
  @ViewChild('folderContextTrigger') folderContextTrigger!: MatMenuTrigger;

  // Menu de contexto (botão direito) sobre uma pasta ou sobre a área da pasta atual
  contextMenuPosition = signal({ x: 0, y: 0 });
  contextTarget = signal<ExplorerTarget | null>(null);
  private uploadTarget: ExplorerTarget | null = null;

  private fileApi = inject(StoredFileApiService);
  private folderApi = inject(StoredFolderApiService);
  private dialogBoxService = inject(AppDialogBoxService);
  private resultService = inject(ResultService);
  private translocoService = inject(TranslocoService);
  uploadService = inject(StoredFileUploadService);

  // Árvore
  treeNodes = signal<ExplorerTreeNode[]>([]);
  selectedNode = signal<ExplorerTreeNode | null>(null);
  treeOpen = signal(true);

  // Navegação
  currentStorage = signal<ExplorerStorage | null>(null);
  currentFolder = signal<StoredFolder | null>(null);
  crumbs = signal<StoredFolder[]>([]);

  // Conteúdo
  folders = signal<StoredFolder[]>([]);
  files = signal<StoredFile[]>([]);
  /** Paginação: pastas primeiro, arquivos na sequência, no máximo PAGE_SIZE itens por página. */
  page = signal(1);
  folderCount = signal(0);
  fileCount = signal(0);
  tree = signal<StoredFolder[]>([]);
  loading = signal(false);
  private loadSequence = 0;

  // Apresentação
  viewMode = signal<ExplorerViewMode>(this.restoreViewMode());
  sortBy = signal<ExplorerSortBy>('name');
  sortAsc = signal(true);
  searchText = signal('');
  selected = signal<ExplorerItem | null>(null);
  detailsOpen = signal(false);

  // Arrastar e soltar
  dragOverArea = signal(false);
  dropTargetId = signal<string | null>(null);
  private draggedItem: ExplorerItem | null = null;

  formatBytes = formatBytes;

  hasDrives = computed(() => this.storages().length > 0);
  /** Storage efetivo das consultas e gravações: disco selecionado ou o fixo do modo embutido. */
  effectiveStorageId = computed(() => this.currentStorage()?.id ?? this.storageId() ?? null);
  ownerless = computed(() => !this.feature() && !((this.parentNid() ?? 0) > 0));

  /** Pastas primeiro, depois arquivos; ambos filtrados pelo texto e ordenados pelo critério atual. */
  items = computed<ExplorerItem[]>(() => {
    const text = this.searchText().trim().toLowerCase();
    const folders = this.folders().map(f => this.toItem(f));
    const files = this.files().map(f => this.toItem(f));
    const filter = (i: ExplorerItem) => !text || i.name.toLowerCase().includes(text) || (i.typeName ?? '').toLowerCase().includes(text);
    return [...this.sort(folders.filter(filter)), ...this.sort(files.filter(filter))];
  });

  folderItems = computed(() => this.items().filter(i => i.kind === 'folder'));
  fileItems = computed(() => this.items().filter(i => i.kind === 'file'));
  totalSizeBytes = computed(() => this.files().reduce((sum, f) => sum + (f.sizeBytes ?? 0), 0));

  readonly pageSize = PAGE_SIZE;
  totalCount = computed(() => this.folderCount() + this.fileCount());
  pageCount = computed(() => Math.max(1, Math.ceil(this.totalCount() / PAGE_SIZE)));
  pageStart = computed(() => (this.totalCount() ? (this.page() - 1) * PAGE_SIZE + 1 : 0));
  pageEnd = computed(() => Math.min(this.page() * PAGE_SIZE, this.totalCount()));

  private treeContextKey = '';

  constructor() {
    // Discos ou filtros de vínculo mudaram: reconstrói a árvore (ou volta à raiz no modo embutido).
    // Se só os totais dos mesmos discos mudaram (após upload/exclusão), atualiza sem perder a navegação.
    effect(() => {
      const storages = this.storages();
      const contextKey = `${this.feature() ?? ''}|${this.parentNid() ?? ''}|${this.storageId() ?? ''}`;
      untracked(() => {
        const sameContext = contextKey === this.treeContextKey;
        this.treeContextKey = contextKey;

        if (!storages.length) { this.treeNodes.set([]); this.selectedNode.set(null); this.currentStorage.set(null); this.goToRoot(); return; }
        if (sameContext && this.hasSameDrives(storages)) this.refreshStorages(storages);
        else this.buildTree(storages);
      });
    });
  }

  // ---------------------------------------------------------------------------------------
  // Árvore (painel esquerdo)

  private hasSameDrives(storages: ExplorerStorage[]): boolean {
    const nodes = this.treeNodes();
    return nodes.length === storages.length && nodes.every(n => storages.some(s => s.id === n.id));
  }

  /** Troca a referência do storage em toda a subárvore de cada disco e no disco corrente. */
  private refreshStorages(storages: ExplorerStorage[]): void {
    const apply = (node: ExplorerTreeNode, storage: ExplorerStorage) => {
      node.storage = storage;
      for (const child of node.children ?? []) apply(child, storage);
    };

    for (const node of this.treeNodes()) {
      const storage = storages.find(s => s.id === node.id);
      if (storage) apply(node, storage);
    }

    const current = storages.find(s => s.id === this.currentStorage()?.id);
    if (current) this.currentStorage.set(current);
    this.treeNodes.update(list => [...list]);
  }

  private buildTree(storages: ExplorerStorage[]): void {
    const nodes: ExplorerTreeNode[] = storages.map(s => ({
      kind: 'storage', id: s.id ?? '', name: s.name ?? '', storage: s, folder: null, parent: null, children: null, expanded: false, loading: false,
    }));
    this.treeNodes.set(nodes);

    const preferred = nodes.find(n => n.id === (this.currentStorage()?.id ?? this.storageId())) ?? nodes[0];
    this.selectNode(preferred);
    void this.toggleNode(preferred, true);
  }

  selectNode(node: ExplorerTreeNode): void {
    this.selectedNode.set(node);
    this.currentStorage.set(node.storage);
    this.currentFolder.set(node.folder);
    this.crumbs.set(this.chainOf(node));
    this.selected.set(null);
    this.searchText.set('');
    this.page.set(1);
    this.load();
  }

  async toggleNode(node: ExplorerTreeNode, forceExpand = false): Promise<void> {
    const expand = forceExpand || !node.expanded;
    node.expanded = expand;
    if (expand && node.children === null) await this.loadChildren(node);
    this.treeNodes.update(list => [...list]);
  }

  private async loadChildren(node: ExplorerTreeNode): Promise<void> {
    node.loading = true;
    this.treeNodes.update(list => [...list]);
    try {
      const list = await firstValueFrom(this.folderApi.list({
        storageId: node.storage.id, feature: this.feature(), parentNid: this.parentNid(), folderId: node.folder?.id ?? null,
      }));
      node.children = this.toChildNodes(node, list ?? []);
    } catch (error) {
      this.resultService.showError(error);
      node.children = [];
    } finally {
      node.loading = false;
      this.treeNodes.update(list => [...list]);
    }
  }

  private toChildNodes(parent: ExplorerTreeNode, folders: StoredFolder[]): ExplorerTreeNode[] {
    const previous = new Map((parent.children ?? []).map(c => [c.id, c]));
    return folders.map(f => {
      const old = previous.get(f.id!);
      return { kind: 'folder', id: f.id!, name: f.name ?? '', storage: parent.storage, folder: f, parent, children: old?.children ?? null, expanded: old?.expanded ?? false, loading: false };
    });
  }

  private chainOf(node: ExplorerTreeNode): StoredFolder[] {
    const chain: StoredFolder[] = [];
    for (let n: ExplorerTreeNode | null = node; n; n = n.parent) if (n.folder) chain.unshift(n.folder);
    return chain;
  }

  /** Após carregar o conteúdo, sincroniza os filhos do nó atual com as pastas listadas. */
  private syncTreeWithContent(): void {
    const node = this.selectedNode();
    if (!node) return;
    node.children = this.toChildNodes(node, this.folders());
    if (node.children.length) node.expanded = true;
    this.treeNodes.update(list => [...list]);
  }

  isNodeSelected(node: ExplorerTreeNode): boolean {
    return this.selectedNode() === node;
  }

  storageUsagePercent(storage: ExplorerStorage | null): number {
    if (!storage?.totalBytes) return 0;
    return Math.min(100, Math.round(((storage.usedBytes ?? 0) / storage.totalBytes) * 100));
  }

  storageIcon(storage: ExplorerStorage): string {
    return storage.isDefault ? 'solar:server-2-linear' : 'solar:database-line-duotone';
  }

  private rootOf(node: ExplorerTreeNode): ExplorerTreeNode {
    let n = node;
    while (n.parent) n = n.parent;
    return n;
  }

  // ---------------------------------------------------------------------------------------
  // Navegação

  goToRoot(): void {
    const node = this.selectedNode();
    if (node) { this.selectNode(this.rootOf(node)); return; }
    this.currentFolder.set(null);
    this.crumbs.set([]);
    this.selected.set(null);
    this.page.set(1);
    this.load();
  }

  goToPage(page: number): void {
    const target = Math.min(Math.max(1, page), this.pageCount());
    if (target === this.page()) return;
    this.page.set(target);
    this.selected.set(null);
    this.load();
  }

  openFolder(folder: StoredFolder): void {
    const node = this.selectedNode();
    if (node) {
      node.children ??= this.toChildNodes(node, this.folders());
      node.expanded = true;
      let child = node.children.find(c => c.id === folder.id);
      if (!child) { child = this.toChildNodes(node, [folder])[0]; node.children.push(child); }
      this.selectNode(child);
      void this.toggleNode(child, true);
      return;
    }
    this.currentFolder.set(folder);
    this.crumbs.update(list => [...list, folder]);
    this.selected.set(null);
    this.searchText.set('');
    this.page.set(1);
    this.load();
  }

  goToCrumb(index: number): void {
    const node = this.selectedNode();
    if (node) {
      let target: ExplorerTreeNode | null = node;
      const steps = this.crumbs().length - 1 - index;
      for (let i = 0; i < steps && target; i++) target = target.parent;
      if (target) { this.selectNode(target); return; }
    }
    const crumbs = this.crumbs().slice(0, index + 1);
    this.crumbs.set(crumbs);
    this.currentFolder.set(crumbs[crumbs.length - 1] ?? null);
    this.selected.set(null);
    this.page.set(1);
    this.load();
  }

  goUp(): void {
    if (!this.crumbs().length) return;
    this.goToCrumb(this.crumbs().length - 2);
  }

  load(): void {
    const feature = this.feature();
    const parentNid = this.parentNid();
    const storageId = this.effectiveStorageId();
    const folderId = this.currentFolder()?.id ?? null;

    if (this.hasDrives() && !storageId) {
      this.folders.set([]);
      this.files.set([]);
      return;
    }

    // Navegar rápido entre pastas dispara buscas concorrentes; só a última pode ser aplicada,
    // senão a resposta atrasada da pasta anterior é gravada como conteúdo da pasta atual.
    const request = ++this.loadSequence;

    // A página cobre as duas listas em sequência: as pastas ocupam o começo e os arquivos
    // continuam de onde elas pararam, por isso o offset dos arquivos depende do total de pastas.
    const offset = (this.page() - 1) * PAGE_SIZE;

    this.loading.set(true);
    this.folderApi.listPage({ storageId, feature, parentNid, folderId, offset, limit: PAGE_SIZE }).pipe(
      switchMap(folderResult => {
        const folders = folderResult?.items ?? [];
        const folderCount = folderResult?.count ?? 0;
        const remaining = PAGE_SIZE - folders.length;
        const fileOffset = Math.max(0, offset - folderCount);
        const files$ = this.fileApi.listPage({
          storageId, feature, parentNid, folderId, rootOnly: !folderId,
          offset: fileOffset, limit: remaining > 0 ? remaining : 1,
        });
        return files$.pipe(switchMap(fileResult => of({ folders, folderCount, fileResult, remaining })));
      }),
    ).subscribe({
      next: ({ folders, folderCount, fileResult, remaining }) => {
        if (request !== this.loadSequence) return;
        this.folders.set(folders);
        this.files.set(remaining > 0 ? fileResult?.items ?? [] : []);
        this.folderCount.set(folderCount);
        this.fileCount.set(fileResult?.count ?? 0);
        this.refreshSelected();
        this.syncTreeWithContent();
        this.loading.set(false);

        // Exclusões podem esvaziar a última página; recua e recarrega.
        if (this.page() > this.pageCount()) this.goToPage(this.pageCount());
      },
      error: error => {
        if (request !== this.loadSequence) return;
        this.resultService.showError(error);
        this.loading.set(false);
      },
    });
  }

  private refreshSelected(): void {
    const current = this.selected();
    if (!current) return;
    const next = this.items().find(i => i.id === current.id) ?? null;
    this.selected.set(next);
  }

  // ---------------------------------------------------------------------------------------
  // Seleção e visualização

  select(item: ExplorerItem): void {
    this.selected.set(item);
  }

  open(item: ExplorerItem): void {
    if (item.kind === 'folder') this.openFolder(item.folder!);
    else this.download(item.file!);
  }

  toggleDetails(): void {
    this.detailsOpen.update(v => !v);
  }

  toggleTree(): void {
    this.treeOpen.update(v => !v);
  }

  setViewMode(mode: ExplorerViewMode): void {
    this.viewMode.set(mode);
    try { localStorage.setItem(VIEW_MODE_STORAGE_KEY, mode); } catch { /* storage indisponível */ }
  }

  setSort(by: ExplorerSortBy): void {
    if (this.sortBy() === by) this.sortAsc.update(v => !v);
    else { this.sortBy.set(by); this.sortAsc.set(true); }
  }

  private restoreViewMode(): ExplorerViewMode {
    try {
      const saved = localStorage.getItem(VIEW_MODE_STORAGE_KEY) as ExplorerViewMode | null;
      return saved && ['list', 'details', 'icons', 'tiles'].includes(saved) ? saved : 'details';
    } catch {
      return 'details';
    }
  }

  private sort(list: ExplorerItem[]): ExplorerItem[] {
    const dir = this.sortAsc() ? 1 : -1;
    const by = this.sortBy();
    return [...list].sort((a, b) => {
      let r = 0;
      if (by === 'size') r = a.sizeBytes - b.sizeBytes;
      else if (by === 'date') r = (a.date ?? '').localeCompare(b.date ?? '');
      if (r === 0) r = a.name.localeCompare(b.name, undefined, { sensitivity: 'base', numeric: true });
      return r * dir;
    });
  }

  private toItem(source: StoredFolder | StoredFile): ExplorerItem {
    if ('originalName' in source) {
      const file = source as StoredFile;
      return {
        kind: 'file',
        id: file.id!,
        name: file.originalName ?? '',
        sizeBytes: file.sizeBytes ?? 0,
        date: file.log?.updatedAt ?? file.log?.createdAt ?? null,
        icon: this.iconForFile(file),
        typeName: this.typeNameFor(file),
        file,
      };
    }
    const folder = source as StoredFolder;
    return {
      kind: 'folder',
      id: folder.id!,
      name: folder.name ?? '',
      sizeBytes: folder.totalSizeBytes ?? 0,
      date: folder.log?.updatedAt ?? folder.log?.createdAt ?? null,
      icon: 'solar:folder-outline',
      typeName: this.translocoService.translate('Folder'),
      folder,
    };
  }

  /** Rótulo de tipo derivado da extensão/MIME (não há mais cadastro de tipos de arquivo). */
  typeNameFor(file: StoredFile): string | null {
    const ext = (file.extension ?? '').trim();
    if (ext) return ext.toUpperCase();
    return file.contentType ?? null;
  }

  iconForFile(file: StoredFile): string {
    const mime = file.contentType ?? '';
    if (mime.startsWith('image/')) return 'solar:gallery-bold';
    if (mime.startsWith('video/')) return 'solar:videocamera-record-bold';
    if (mime.startsWith('audio/')) return 'solar:music-note-2-bold';
    if (mime === 'application/pdf') return 'solar:document-text-bold';
    if (mime.includes('spreadsheet') || mime.includes('excel') || mime === 'text/csv') return 'solar:chart-square-bold';
    if (mime.includes('presentation') || mime.includes('powerpoint')) return 'solar:presentation-graph-bold';
    if (mime.includes('word')) return 'solar:document-bold';
    if (mime.includes('zip') || mime.includes('compressed') || mime.includes('tar')) return 'solar:zip-file-bold';
    if (mime.startsWith('text/') || mime.includes('json') || mime.includes('xml')) return 'solar:code-file-bold';
    return 'solar:file-bold';
  }

  /** Pasta de sistema (Sistema/{Feature}) recebe cor própria. */
  isSystemFolder(folder: StoredFolder | null | undefined): boolean {
    return folder?.type?.key === 'system';
  }

  /** Pasta ou arquivo do sistema (tipo ou origem) é somente leitura: não renomeia, não move, não exclui. */
  isSystemItem(item: ExplorerItem): boolean {
    return item.kind === 'folder'
      ? this.isProtectedFolder(item.folder)
      : item.file?.origin?.key === 'system';
  }

  isProtectedFolder(folder: StoredFolder | null | undefined): boolean {
    return this.isSystemFolder(folder) || folder?.origin?.key === 'system';
  }

  folderColor(folder: StoredFolder | null | undefined): string {
    return this.isSystemFolder(folder) ? 'text-purple-600' : 'text-amber-500';
  }

  iconColor(item: ExplorerItem): string {
    if (item.kind === 'folder') return this.folderColor(item.folder);
    const mime = item.file?.contentType ?? '';
    if (mime === 'application/pdf') return 'text-red-500';
    if (mime.startsWith('image/')) return 'text-violet-500';
    if (mime.includes('spreadsheet') || mime.includes('excel') || mime === 'text/csv') return 'text-green-600';
    if (mime.includes('word')) return 'text-blue-600';
    if (mime.includes('presentation') || mime.includes('powerpoint')) return 'text-orange-500';
    return 'text-slate-500';
  }

  // ---------------------------------------------------------------------------------------
  // Pastas

  newFolder(target: ExplorerTarget | null = null): void {
    const destination = target ?? this.currentTarget();

    this.dialogBoxService.openInput({
      title: this.translocoService.translate('New folder'),
      label: this.translocoService.translate('Folder name'),
      icon: { name: 'solar:folder-plus-linear', color: 'primary' },
      actions: 'okCancel',
      required: true,
    }).afterClosed().subscribe((result: AppDialogBoxResult) => {
      if (result?.action !== 'ok' || !result.text?.trim()) return;
      this.loading.set(true);
      this.folderApi.create({
        storageId: this.storageIdOf(destination),
        feature: this.feature(),
        parentNid: this.parentNid(),
        folderId: destination.folder?.id ?? null,
        name: result.text.trim(),
      }).subscribe({
        next: () => {
          this.resultService.showSuccess(this.translocoService.translate('Folder created successfully'));
          this.invalidateTree();
          this.load();
        },
        error: error => { this.resultService.showError(error); this.loading.set(false); },
      });
    });
  }

  renameFolder(folder: StoredFolder | null): void {
    if (!folder || this.isProtectedFolder(folder)) return;

    const ref = this.dialogBoxService.openInput({
      title: this.translocoService.translate('Rename'),
      label: this.translocoService.translate('Folder name'),
      icon: { name: 'solar:pen-linear', color: 'primary' },
      actions: 'okCancel',
      required: true,
    });
    ref.componentInstance.result.text = folder.name ?? '';

    ref.afterClosed().subscribe((result: AppDialogBoxResult) => {
      const name = result?.text?.trim();
      if (result?.action !== 'ok' || !name || name === folder.name) return;
      this.loading.set(true);
      this.folderApi.update(folder.id!, { name }).subscribe({
        next: () => {
          this.resultService.showSuccess(this.translocoService.translate('Folder renamed successfully'));
          this.invalidateTree();
          this.load();
        },
        error: error => { this.resultService.showError(error); this.loading.set(false); },
      });
    });
  }

  renameFile(file: StoredFile | null): void {
    if (!file || file.origin?.key === 'system') return;

    const ref = this.dialogBoxService.openInput({
      title: this.translocoService.translate('Rename'),
      label: this.translocoService.translate('File name'),
      icon: { name: 'solar:pen-linear', color: 'primary' },
      actions: 'okCancel',
      required: true,
    });
    ref.componentInstance.result.text = file.originalName ?? '';

    ref.afterClosed().subscribe((result: AppDialogBoxResult) => {
      const name = result?.text?.trim();
      if (result?.action !== 'ok' || !name || name === file.originalName) return;
      this.loading.set(true);
      this.fileApi.rename(file.id!, name).subscribe({
        next: () => {
          this.resultService.showSuccess(this.translocoService.translate('File renamed successfully'));
          this.load();
        },
        error: error => { this.resultService.showError(error); this.loading.set(false); },
      });
    });
  }

  /** Renomeia o item selecionado, pasta ou arquivo. */
  renameItem(item: ExplorerItem): void {
    if (item.kind === 'folder') this.renameFolder(item.folder ?? null);
    else this.renameFile(item.file ?? null);
  }

  deleteFolder(folder: StoredFolder): void {
    if ((folder.folderCount ?? 0) > 0 || (folder.fileCount ?? 0) > 0) {
      this.resultService.showError(this.translocoService.translate('Only empty folders can be deleted.'));
      return;
    }

    this.dialogBoxService.openDelete().afterClosed().subscribe((result: AppDialogBoxResult) => {
      if (result?.action !== 'yes') return;
      this.loading.set(true);
      this.folderApi.delete(folder.id!).subscribe({
        next: () => {
          this.resultService.showSuccess(this.translocoService.translate('Folder deleted successfully'));
          this.load();
        },
        error: error => { this.resultService.showError(error); this.loading.set(false); },
      });
    });
  }

  /** Carrega a árvore inteira do disco/dono para o menu "Mover para". */
  loadTree(): void {
    this.folderApi.list({ storageId: this.effectiveStorageId(), feature: this.feature(), parentNid: this.parentNid(), tree: true }).subscribe({
      next: list => this.tree.set(list ?? []),
      error: error => this.resultService.showError(error),
    });
  }

  /** Destinos válidos para mover `item`: exclui a pasta atual, a própria pasta e seus descendentes. */
  canMoveTo(item: ExplorerItem, target: StoredFolder | null): boolean {
    if (this.isSystemItem(item)) return false;
    const currentId = this.currentFolder()?.id ?? null;
    if ((target?.id ?? null) === currentId) return false;
    if (item.kind === 'folder' && target?.path && item.folder?.path && target.path.startsWith(item.folder.path)) return false;
    return true;
  }

  moveItem(item: ExplorerItem, target: StoredFolder | null): void {
    if (!this.canMoveTo(item, target)) return;

    const request$: Observable<unknown> = item.kind === 'folder'
      ? this.folderApi.move(item.id, { folderId: target?.id ?? null })
      : this.fileApi.move(item.id, { folderId: target?.id ?? null });

    this.loading.set(true);
    request$.subscribe({
      next: () => {
        this.resultService.showSuccess(this.translocoService.translate('Moved successfully'));
        this.invalidateTree();
        this.load();
      },
      error: error => { this.resultService.showError(error); this.loading.set(false); },
    });
  }

  /** Mover altera outras pastas da árvore: recarrega os filhos do disco atual mantendo o nó selecionado. */
  private invalidateTree(): void {
    const current = this.selectedNode();
    if (!current) return;
    const root = this.rootOf(current);
    if (root !== current) root.children = null;
    if (root.expanded) void this.loadChildren(root);
  }

  // ---------------------------------------------------------------------------------------
  // Menu de contexto

  /** Botão direito sobre uma pasta do conteúdo, ou sobre a área vazia (pasta atual). */
  onContextMenu(event: MouseEvent, folder: StoredFolder | null = null): void {
    if (this.readonly()) return;
    this.openContextMenu(event, { storage: this.currentStorage(), folder: folder ?? this.currentFolder() });
  }

  /** Botão direito sobre um nó da árvore (disco ou pasta). */
  onNodeContextMenu(event: MouseEvent, node: ExplorerTreeNode): void {
    if (this.readonly()) return;
    this.openContextMenu(event, { storage: node.storage, folder: node.folder });
  }

  private openContextMenu(event: MouseEvent, target: ExplorerTarget): void {
    event.preventDefault();
    event.stopPropagation();
    this.contextTarget.set(target);
    this.contextMenuPosition.set({ x: event.clientX, y: event.clientY });
    this.folderContextTrigger.closeMenu();
    setTimeout(() => this.folderContextTrigger.openMenu());
  }

  private currentTarget(): ExplorerTarget {
    return { storage: this.currentStorage(), folder: this.currentFolder() };
  }

  private storageIdOf(target: ExplorerTarget): string | null {
    return target.storage?.id ?? this.effectiveStorageId();
  }

  // ---------------------------------------------------------------------------------------
  // Arquivos

  pickFiles(target: ExplorerTarget | null = null): void {
    this.uploadTarget = target;
    this.fileInput.nativeElement.value = '';
    this.fileInput.nativeElement.click();
  }

  onFilesSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    const target = this.uploadTarget ?? this.currentTarget();
    this.uploadTarget = null;
    void this.uploadFiles(Array.from(input.files ?? []), target.folder, target.storage);
  }

  async uploadFiles(selected: File[], target: StoredFolder | null, storage: ExplorerStorage | null = null): Promise<void> {
    if (!selected.length) return;

    const storageId = this.storageIdOf({ storage, folder: target });
    this.loading.set(true);
    let failures = 0;

    for (const file of selected) {
      try {
        await this.uploadService.upload(file, {
          storageId,
          feature: this.feature(),
          parentNid: this.parentNid(),
          type: this.fileType(),
          folderId: target?.id ?? null,
        });
      } catch (error) {
        failures++;
        this.resultService.showError(error);
      }
    }

    this.uploadService.clear();
    this.loading.set(false);
    if (failures < selected.length) {
      this.resultService.showSuccess(this.translocoService.translate('Upload completed successfully'));
      this.usageChanged.emit();
    }
    this.load();
  }

  async copyPublicUrl(file: StoredFile): Promise<void> {
    if (!file.publicUrl) return;
    try {
      await navigator.clipboard.writeText(file.publicUrl);
      this.resultService.showSuccess(this.translocoService.translate('URL copied to clipboard'));
    } catch (error) {
      this.resultService.showError(error);
    }
  }

  download(file: StoredFile): void {
     if (file.publicUrl) {
      window.open(file.publicUrl, '_blank', 'noopener');
      return;
    }
    if (!file.id) return;

    if (!this.canDownload(file)) return;
    this.fileApi.getDownloadUrl(file.id).subscribe({
      next: result => window.open(result.url, '_blank', 'noopener'),
      error: error => this.resultService.showError(error),
    });
  }

  deleteFile(file: StoredFile): void {
    if (!file.id) return;

    this.dialogBoxService.openDelete().afterClosed().subscribe((result: AppDialogBoxResult) => {
      if (result?.action !== 'yes') return;
      this.loading.set(true);
      this.fileApi.delete(file.id!, result.text).subscribe({
        next: () => {
          this.resultService.showSuccess(this.translocoService.translate('Delete completed successfully'));
          this.usageChanged.emit();
          this.load();
        },
        error: error => { this.resultService.showError(error); this.loading.set(false); },
      });
    });
  }

  deleteItem(item: ExplorerItem): void {
    if (item.kind === 'folder') this.deleteFolder(item.folder!);
    else this.deleteFile(item.file!);
  }

  /** Itens do sistema e arquivos fora de disponível/substituído não podem ser excluídos (espelha o backend). */
  canDeleteItem(item: ExplorerItem): boolean {
    if (this.isSystemItem(item)) return false;
    if (item.kind === 'folder') return true;
    const file = item.file!;
    return file.status?.key === 'available' || file.status?.key === 'superseded';
  }

  canDownload(file: StoredFile): boolean {
    return file.status?.key === 'available' || file.status?.key === 'superseded';
  }

  // ---------------------------------------------------------------------------------------
  // Arrastar e soltar (arquivos externos para upload; itens internos para mover)

  onAreaDragOver(event: DragEvent): void {
    if (!this.hasExternalFiles(event) || this.readonly()) return;
    event.preventDefault();
    this.dragOverArea.set(true);
  }

  onAreaDragLeave(event: DragEvent): void {
    if (event.currentTarget === event.target) this.dragOverArea.set(false);
  }

  onAreaDrop(event: DragEvent): void {
    this.dragOverArea.set(false);
    if (!this.hasExternalFiles(event) || this.readonly()) return;
    event.preventDefault();
    void this.uploadFiles(Array.from(event.dataTransfer?.files ?? []), this.currentFolder());
  }

  onItemDragStart(event: DragEvent, item: ExplorerItem): void {
    if (this.readonly() || this.isSystemItem(item)) { event.preventDefault(); return; }
    this.draggedItem = item;
    event.dataTransfer?.setData('text/plain', item.id);
    if (event.dataTransfer) event.dataTransfer.effectAllowed = 'move';
  }

  onItemDragEnd(): void {
    this.draggedItem = null;
    this.dropTargetId.set(null);
  }

  /** Pasta (breadcrumb, lista ou árvore) como alvo: aceita item interno para mover ou arquivos externos para upload. */
  onTargetDragOver(event: DragEvent, target: StoredFolder | null): void {
    if (this.readonly()) return;
    const targetId = target?.id ?? 'root';
    if (this.draggedItem) {
      if (this.draggedItem.id === target?.id || !this.canMoveTo(this.draggedItem, target)) return;
      event.preventDefault();
      event.stopPropagation();
      if (event.dataTransfer) event.dataTransfer.dropEffect = 'move';
      this.dropTargetId.set(targetId);
    } else if (this.hasExternalFiles(event)) {
      event.preventDefault();
      event.stopPropagation();
      this.dropTargetId.set(targetId);
    }
  }

  onTargetDragLeave(): void {
    this.dropTargetId.set(null);
  }

  onTargetDrop(event: DragEvent, target: StoredFolder | null): void {
    event.preventDefault();
    event.stopPropagation();
    this.dropTargetId.set(null);
    this.dragOverArea.set(false);

    if (this.draggedItem) {
      const item = this.draggedItem;
      this.draggedItem = null;
      this.moveItem(item, target);
      return;
    }

    if (this.hasExternalFiles(event)) {
      void this.uploadFiles(Array.from(event.dataTransfer?.files ?? []), target);
    }
  }

  private hasExternalFiles(event: DragEvent): boolean {
    return !this.draggedItem && !!event.dataTransfer && Array.from(event.dataTransfer.types).includes('Files');
  }

  isDropTarget(target: StoredFolder | null): boolean {
    return this.dropTargetId() === (target?.id ?? 'root');
  }
}
